package com.trevorwiebe.timesheet.settlements.data

import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.data.NumberCounter
import com.trevorwiebe.timesheet.core.data.Utils.errorWrapper
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.TSResult
import com.trevorwiebe.timesheet.settlements.data.dto.SendSettlementRequest
import com.trevorwiebe.timesheet.settlements.data.dto.SendSettlementResponse
import com.trevorwiebe.timesheet.settlements.data.dto.SettledLoadDto
import com.trevorwiebe.timesheet.settlements.data.dto.SettlementDto
import com.trevorwiebe.timesheet.settlements.data.dto.SettlementIdRequest
import com.trevorwiebe.timesheet.settlements.data.dto.SettlementPdfResponse
import com.trevorwiebe.timesheet.settlements.data.dto.toSettlement
import com.trevorwiebe.timesheet.settlements.data.dto.toSettlementDto
import com.trevorwiebe.timesheet.settlements.domain.SettlementRepository
import com.trevorwiebe.timesheet.settlements.domain.model.Settlement
import com.trevorwiebe.timesheet.settlements.domain.model.SettlementStatus
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.DocumentReference
import dev.gitlive.firebase.firestore.DocumentSnapshot
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.functions.functions
import dev.gitlive.firebase.storage.storage
import kotlinx.coroutines.TimeoutCancellationException
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.withTimeout
import kotlinx.datetime.LocalDate
import kotlin.time.Duration.Companion.seconds

const val FIRST_SETTLEMENT_NUMBER = 1001

class SettlementRepositoryImpl(
    private val firebaseDatabase: Firebase,
    private val coreRepository: CoreRepository,
) : SettlementRepository {

    private fun org(orgId: String) = firebaseDatabase.firestore.collection("organizations").document(orgId)

    private fun lockRef(orgId: String, loadId: String): DocumentReference =
        org(orgId).collection("settledLoads").document(loadId)

    override suspend fun getSettlements(): Flow<TSResult> {
        val orgIdResult = coreRepository.getOrganizationId()
        if (orgIdResult.error != null) return flow { emit(orgIdResult) }
        val orgId = orgIdResult.data as String

        val flow = org(orgId).collection("settlements").snapshots
            .map { snapshot ->
                snapshot.documents
                    .map { doc -> doc.data<SettlementDto>().toSettlement(id = doc.id) }
                    .sortedByDescending { it.number }
            }
            .map { TSResult(data = it) }
        return FirestoreListenerRegistry.registerFlow(flow)
    }

    override suspend fun createSettlement(settlement: Settlement): TSResult = errorWrapper {
        val orgId = coreRepository.getOrganizationId().data as String
        val counterRef = org(orgId).collection("counters").document("settlements")
        val settlementRef = org(orgId).collection("settlements").document
        val lockRefs = settlement.loadIds.map { lockRef(orgId, it) }

        val saved = firebaseDatabase.firestore.runTransaction {
            val (number, counter) = settlementCounter(get(counterRef)).claim()
            // All reads must happen before any write in a Firestore transaction.
            val alreadySettled = lockRefs.count { get(it).exists }
            check(alreadySettled == 0) {
                "$alreadySettled of these loads are already on another settlement. Refresh and try again."
            }
            val numbered = settlement.copy(id = settlementRef.id, number = number, status = SettlementStatus.DRAFT)
            set(counterRef, NumberCounter.serializer(), counter)
            set(settlementRef, SettlementDto.serializer(), numbered.toSettlementDto())
            lockRefs.forEach { set(it, SettledLoadDto.serializer(), SettledLoadDto(settlementId = settlementRef.id)) }
            numbered
        }
        TSResult(data = saved)
    }

    override suspend fun updateDraftSettlement(settlement: Settlement): TSResult = errorWrapper {
        val orgId = coreRepository.getOrganizationId().data as String
        val settlementRef = org(orgId).collection("settlements").document(settlement.id)
        firebaseDatabase.firestore.runTransaction {
            val current = get(settlementRef)
            check(current.exists) { "This settlement no longer exists." }
            val stored = current.data<SettlementDto>()
            check(stored.status == SettlementStatus.DRAFT.name) { "Sent settlements can't be changed." }
            // The PDF path is the server's to set; keep whatever it has stored.
            set(settlementRef, SettlementDto.serializer(), settlement.toSettlementDto().copy(pdfPath = stored.pdfPath))
        }
        TSResult(data = settlement.id)
    }

    override suspend fun deleteDraftSettlement(settlement: Settlement): TSResult = errorWrapper {
        val orgId = coreRepository.getOrganizationId().data as String
        val settlementRef = org(orgId).collection("settlements").document(settlement.id)
        val counterRef = org(orgId).collection("counters").document("settlements")
        firebaseDatabase.firestore.runTransaction {
            val current = get(settlementRef)
            val counter = settlementCounter(get(counterRef))
            check(current.exists) { "This settlement no longer exists." }
            val stored = current.data<SettlementDto>()
            check(stored.status == SettlementStatus.DRAFT.name) { "Sent settlements can't be deleted." }
            delete(settlementRef)
            stored.lines.forEach { delete(lockRef(orgId, it.loadId)) }
            set(counterRef, NumberCounter.serializer(), counter.release(stored.number))
        }
        TSResult(data = settlement.id)
    }

    override suspend fun markSettlementSent(settlement: Settlement, sentOn: LocalDate, sentTo: String): TSResult = errorWrapper {
        val orgId = coreRepository.getOrganizationId().data as String
        val settlementRef = org(orgId).collection("settlements").document(settlement.id)
        firebaseDatabase.firestore.runTransaction {
            val current = get(settlementRef)
            check(current.exists) { "This settlement no longer exists." }
            check(current.data<SettlementDto>().status == SettlementStatus.DRAFT.name) {
                "This settlement was already sent."
            }
            update(
                settlementRef,
                "status" to SettlementStatus.SENT.name,
                "sentAt" to sentOn.toString(),
                "sentTo" to sentTo,
            )
        }
        TSResult(data = settlement.id)
    }

    override suspend fun generateSettlementPdf(settlementId: String): TSResult = slowCall {
        val result = Firebase.functions.httpsCallable("generateSettlementPdf").invoke(SettlementIdRequest(settlementId))
        TSResult(data = result.data<SettlementPdfResponse>().pdfPath)
    }

    override suspend fun getPdfUrl(pdfPath: String): TSResult = errorWrapper {
        TSResult(data = Firebase.storage.reference.child(pdfPath).getDownloadUrl())
    }

    override suspend fun sendSettlement(settlementId: String, to: String): TSResult = slowCall {
        val result = Firebase.functions.httpsCallable("sendSettlement").invoke(SendSettlementRequest(settlementId, to))
        TSResult(data = result.data<SendSettlementResponse>())
    }

    private fun settlementCounter(snapshot: DocumentSnapshot): NumberCounter =
        if (snapshot.exists) snapshot.data<NumberCounter>()
        else NumberCounter(FIRST_SETTLEMENT_NUMBER)

    /** Like errorWrapper, but allows longer: a cold function rendering a PDF can outlast its 15 seconds. */
    private suspend fun slowCall(action: suspend () -> TSResult): TSResult = try {
        withTimeout(110.seconds) { action() }
    } catch (e: TimeoutCancellationException) {
        TSResult(error = "This is taking longer than expected. Check the settlement before trying again.")
    } catch (e: Exception) {
        TSResult(error = e.message ?: e.toString())
    }
}
